import { SerializedStyles, css } from '@emotion/react'
import SearchIcon from '@mui/icons-material/Search'
import { debounce as _debounce } from 'lodash-es'
import {
    CSSProperties,
    KeyboardEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { updateBlur, updateFocus } from '../../../state/state.layout'
import { addSearchHistoryItem } from '../../../state/state.search'
import ButtonFilter from '../../Buttons/ButtonFilter'
import ButtonX from '../../Buttons/ButtonX'
import IconButton from '../../Buttons/IconButton'
import Input from '../../common/Input'
import { palette } from '../../styles/colors'
import { COMPONENT_HEIGHTS } from '../../theme'
import InputHistory from './InputHistory'

const INPUT_SEARCH_HEIGHT = COMPONENT_HEIGHTS.inputSearch
const ROOT_PADDING = 0
const ROOT_BORDER_WIDTH = 1
const INPUT_SEARCH_DELAY_FACTOR = 3.5

const inputSearchRootStyles = css({
    font: 'inherit',
    alignItems: 'center',
    border: `${ROOT_BORDER_WIDTH}px solid ${palette.drawerDivider}`,
    display: 'flex',
    flex: 'auto',
    fontSize: '1rem',
    height: INPUT_SEARCH_HEIGHT,
    padding: ROOT_PADDING / 2,
    width: '100%',
    position: 'relative' as const,
})
const inputBaseRootStyles = css({
    flex: 1,
    height: '100%',
    font: 'inherit',
    fontSize: 14.5,
})
const searchButtonStyles = css({
    padding: 5,
    border: 0,
    minWidth: 34,
})
const inputSearchIconStyles = css({
    color: palette.contrastIcons,
})
const eraseButtonStyles = css({
    '& svg': {
        color: palette.headerText,
    },
})
const endAdornmentStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
})

const emptyFn = () => {}

interface IInputSearch {
    style?: CSSProperties
    page?: string
    autoFocus?: boolean
    quietFocus?: boolean
    debounce?: boolean
    debounceDelay?: number
    defaultValue?: string
    historyId?: string
    persistent?: boolean
    historyMax?: number
    inputCss?: SerializedStyles
    isFilterActive?: boolean
    placeholder?: string
    showSearchIcon?: boolean
    clearInputIcon?: string
    clearInputIconColor?: string
    skipInputValueUpdate?: boolean
    showClearButton?: boolean
    // showMissMFCOnlineOnly?: boolean
    // showMissMFCOnlineOnlyToggle?: boolean
    // onAddHistoryItem?(history?: Array<any>): void
    onDeleteHistoryItem?(item: string, history?: Array<string>): void
    onEmptyHistory?(history?: Array<any>): void
    onInputRef?(ref: HTMLInputElement): void
    onChange(query: string): void
    onClear?(): void
    // setShowMissMFCOnlineOnly?(): void
    onClickFilter?(event: any, isActive?: boolean, inputRef?: any): void
    onSelectHistory?(item: string): void
}

const InputSearch = memo((props: IInputSearch) => {
    const {
        page,
        style,
        inputCss,
        autoFocus,
        quietFocus,
        debounce = true,
        debounceDelay = 325,
        defaultValue,
        historyId, // this should change to "hasHistory"
        isFilterActive = false,
        placeholder = '',
        showSearchIcon = true,
        clearInputIcon,
        clearInputIconColor,
        skipInputValueUpdate,
        showClearButton,
        // showMissMFCOnlineOnly = false,
        // showMissMFCOnlineOnlyToggle = false,
        // setShowMissMFCOnlineOnly = () => {},
        // onAddHistoryItem = () => {},
        onDeleteHistoryItem = emptyFn,
        onEmptyHistory = emptyFn,
        onSelectHistory,
        onInputRef,
        onChange,
        onClear,
        onClickFilter,
    } = props

    const historyRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef(null)
    const lastUpdate = useRef(null)

    const [delayShift, setDelayShift] = useState(1)
    const [historyAutofocus, setHistoryAutofocus] = useState(false)
    const [isHistoryOpen, setIsHistoryOpen] = useState(false)
    const [showErase, setShowErase] = useState(showClearButton || !!onClear)

    useEffect(() => {
        if (showClearButton === undefined) {
            return
        }

        setShowErase(showClearButton)
    }, [showClearButton])

    const handleOnChange = useCallback(
        (value: string, history?: boolean) => {
            // if the input is unmounted before the debounce'd handleOnChange
            // do not set data-search-value
            if (historyRef.current)
                historyRef.current.dataset.searchValue = value
            const trimmedValue = value?.trim() || ''
            if (historyId && trimmedValue.length > 1) {
                addSearchHistoryItem(trimmedValue)
            }

            if (history && typeof onSelectHistory === 'function') {
                onSelectHistory(value)
                return
            }

            onChange(value)
        },
        [historyId, onChange],
    )

    // const debouncedAddHistoryItem = useMemo(
    //     () => _debounce(addHistoryItem, HISTORY_ADD_ITEM_DELAY),
    //     [addHistoryItem],
    // )

    const debouncedHandleOnChange = useMemo(
        () => _debounce(handleOnChange, debounceDelay * delayShift),
        [handleOnChange, debounceDelay, delayShift],
    )

    useEffect(() => {
        if (!debouncedHandleOnChange) {
            return
        }

        return () => {
            debouncedHandleOnChange.cancel()
        }
    }, [debouncedHandleOnChange])

    const updateValue = useCallback(
        (value = '') => {
            setIsHistoryOpen(false)
            if (value === lastUpdate.current) return

            setShowErase(() => {
                const overrideSkip =
                    !value &&
                    lastUpdate.current !== null &&
                    value !== lastUpdate.current

                lastUpdate.current = value
                if (!skipInputValueUpdate) {
                    inputRef.current.value = value
                }

                // don't trigger on change until it's got a value
                if (debounce && (value || overrideSkip)) {
                    debouncedHandleOnChange(value)
                }

                return value?.trim() !== '' || !!onClear
            })
        },
        [debounce, skipInputValueUpdate, debouncedHandleOnChange, onClear],
    )

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            switch (e.key) {
                case 'Enter':
                    handleOnChange(inputRef.current.value)
                    break
                case 'ArrowDown':
                    setHistoryAutofocus(() => {
                        setIsHistoryOpen(true)
                        return true
                    })
                    break
                // NOTE_2021_05_07 (Jose Camporro):
                // Falling through on purpose
                case 'Backspace':
                case 'ArrowLeft':
                case 'ArrowRight':
                    setDelayShift(INPUT_SEARCH_DELAY_FACTOR)
                    break
                default:
                    return
            }
        },
        [handleOnChange, setDelayShift, setHistoryAutofocus],
    )

    const handleSearchButtonClick = useCallback(
        () => handleOnChange(inputRef.current.value),
        [handleOnChange],
    )

    const handleDeleteHistoryItem = useCallback(
        (deletedItem: string, history?: Array<string>) => {
            onDeleteHistoryItem(deletedItem, history)

            if (inputRef?.current.value === deletedItem) {
                updateValue('')
            }
        },
        [onDeleteHistoryItem, updateValue],
    )

    const handleEmptyHistory = useCallback(
        (history?: Array<any>) => {
            onEmptyHistory(history)
            updateValue('')
        },
        [onEmptyHistory, updateValue],
    )

    const handleOnClear = useCallback(
        (event: any) => {
            event.stopPropagation()
            updateValue('')
            onClear?.()
        },
        [updateValue, onClear],
    )

    const handleFilterClick = useCallback(
        (event: any, isActive = false) =>
            onClickFilter(event, isActive, inputRef),
        [onClickFilter],
    )

    const onHistoryItemClick = useCallback(
        (_event: any, value: string) => {
            handleOnChange(value, true)
        },
        [handleOnChange],
    )

    const onInputClick = useCallback(() => {
        setIsHistoryOpen(true)
    }, [])

    const onInputChange = useCallback(
        ({ target: { value } }) => updateValue(value),
        [updateValue],
    )

    const onFocus = useCallback(() => {
        updateFocus(quietFocus)
    }, [quietFocus])

    const onBlur = useCallback(() => {
        updateBlur(quietFocus)
    }, [quietFocus])

    const startAdornment = showSearchIcon && (
        <IconButton
            css={searchButtonStyles}
            onClick={handleSearchButtonClick}
            title={'Submit Search'}
        >
            <SearchIcon data-testid="SearchIcon" css={inputSearchIconStyles} />
        </IconButton>
    )

    const endAdornment = (
        <div css={endAdornmentStyles}>
            {showErase && (
                <ButtonX
                    overrides={eraseButtonStyles}
                    data-testid="Button__ClearSearch"
                    onClick={handleOnClear}
                    clearInputIcon={clearInputIcon}
                    clearInputIconColor={clearInputIconColor}
                />
            )}
            {!!onClickFilter && (
                <ButtonFilter
                    isActive={isFilterActive}
                    onClick={handleFilterClick}
                    size={1}
                />
            )}
        </div>
    )

    useEffect(() => {
        if (typeof defaultValue === 'string') {
            updateValue(defaultValue)
        }
    }, [defaultValue])

    const inputRefCallback = useCallback(
        (ref) => {
            inputRef.current = ref
            if (typeof onInputRef === 'function') {
                onInputRef(ref)
            }
        },
        [onInputRef],
    )

    return (
        <div
            css={inputSearchRootStyles}
            ref={historyRef}
            style={style}
            data-testid="App__Search"
        >
            <Input
                data-testid="searchField"
                endAdornment={endAdornment}
                ref={inputRefCallback}
                placeholder={placeholder}
                startAdornment={startAdornment}
                onChange={onInputChange}
                onClick={onInputClick}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
                autoFocus={autoFocus}
                quietFocus={quietFocus}
                css={[inputBaseRootStyles, inputCss]}
            />
            {/* {showMissMFCOnlineOnlyToggle && (
                <ShowOnlineOnlyFab
                    displayText={'Online'}
                    showOnline={showMissMFCOnlineOnly}
                    setShowOnlineOnly={setShowMissMFCOnlineOnly}
                />
            )} */}
            {!!historyId && (
                <InputHistory
                    anchorEl={historyRef.current}
                    autoFocus={historyAutofocus}
                    historyId={historyId}
                    inputRef={inputRef}
                    open={isHistoryOpen}
                    page={page}
                    onDeleteHistoryItem={handleDeleteHistoryItem}
                    onEmptyHistory={handleEmptyHistory}
                    onItemClick={onHistoryItemClick}
                    setOpenHistory={setIsHistoryOpen}
                />
            )}
        </div>
    )
})

export default InputSearch
